import PropTypes from 'prop-types';
import {useCallback, useRef} from 'react';
import {connect} from 'react-redux';
import Legend from '../../../../components/Accessibility/Legend/Legend';
import MessageType from '../../../../enums/MessageType';
import Status from '../../../../enums/Status';
import useTestableRef from '../../../../hooks/useTestableRef';
import Button from '../../../../UI/Button/Button';
import {
  createAccount,
  setCreateAccountStatus,
  updateCreateAccountDetails,
} from '../../../../v2/actions/createAccountActions';
import Message from '../../../../v2/components/ui/Message/Message';
import FieldContainer2 from '../../../../v2/form/FieldContainer2';
import InputField from '../../../../v2/form/InputField';
import Translation from '../../../../v2/lang/Translation';
import {getPasswordRules, isNewGuestUpdatedPage} from '../../../../v2/selectors/selectors';
import bigQueryMetricRecorder from '../../../../v2/tracking/bigQueryMetricRecorder';
import Track from '../../../../v2/utils/tracking';
import getFullNameFieldDefinition from '../CreateAccountUtils/getFullNameFieldDefinition';
import getPasswordFieldDefinition from '../CreateAccountUtils/getPasswordFieldDefinition';
import AccountBenefits from './AccountBenefits';
import './CreateAccountForm.scss';

const prefixErrors = errors => errors?.map(error => ({
  fieldName: `createAccountForm.${error.fieldName}`,
  errorCode: error.errorCode,
}));

export function CreateAccountForm(props) {
  const fieldContainerRef = useTestableRef(null);
  
  const validate = useCallback(() => {
    if (!fieldContainerRef.current) return {isValid: true};
    
    const containerValidationResult = fieldContainerRef.current.validate();
    
    return {
      isValid: containerValidationResult.isValid,
      errors: prefixErrors(containerValidationResult.errors),
    };
  }, [fieldContainerRef]);
  
  const handleButtonClick = () => {
    if (!props.expandForm) {
      props.setCreateAccountStatus(Status.STARTED);
      return;
    }
    
    const validationResult = validate();
    if (validationResult.isValid) {
      props.createAccount();
      try {
        bigQueryMetricRecorder(
          'CreateAccount',
          [{name: 'create-account-status', value: props.status}],
        );
      } catch (error) {
        Track.track(error);
      }
    }
    
    try {
      if (validationResult.errors) {
        bigQueryMetricRecorder(
          'CreateAccount',
          ['create-account-status'], validationResult.errors.map(error => ({
            type: error.errorCode,
            label: error.fieldName,
          })), 'click',
        );
      }
    } catch (error) {
      Track.track(error);
    }
  };
  
  return (
    <div className="CreateAccountForm" >
      {props.showError && (
        <Message type={MessageType.ERROR}>
          <Translation>checkout.error.error</Translation>
        </Message>
      )}
      
      <AccountBenefits />
      
      <fieldset className="CreateAccountForm__fieldContainer">
        <Legend hidden>
          <Translation>btn.create.account</Translation>
        </Legend>
        {props.expandForm && (
          <FieldContainer2 fieldUpdateDispatcher={props.updateCreateAccountDetails}>
            {(fieldContainer) => {
              fieldContainerRef.current = fieldContainer;
              
              const commonProps = {
                fieldContainer,
                fieldNamePrefix: 'createAccount',
              };
              
              return (
                <>
                  {!props.isNewGuestUpdatedPage && (
                    <InputField
                      {...commonProps}
                      value={props.fullName}
                      definedField={getFullNameFieldDefinition()}
                      legacyNames={{wrappedClassName: 'fullname'}}
                    />
                  )}
                  
                  <InputField
                    {...commonProps}
                    value={props.password}
                    definedField={getPasswordFieldDefinition(props.passwordRules)}
                    legacyNames={{wrappedClassName: 'password'}}
                  />
                </>
              );
            }}
          </FieldContainer2>
        )}
        <div className="CreateAccountForm__button">
          <Button
            id="CreateAccountForm__createAccountButton"
            fullWidth
            primary
            onClick={handleButtonClick}
          >
            <Translation>btn.create.account</Translation>
          </Button>
        </div>
      </fieldset>
    </div>
  );
}

const mapStateToProps = (state) => {
  const {status} = state.createAccount;
  return {
    status: state.createAccount.status,
    fullName: state.createAccount.fullName,
    password: state.createAccount.password,
    showError: status === Status.ERROR,
    expandForm: status === Status.STARTED || status === Status.ERROR,
    isNewGuestUpdatedPage: isNewGuestUpdatedPage(state),
    passwordRules: getPasswordRules(state),
  };
};

CreateAccountForm.propTypes = {
  fullName: PropTypes.string.isRequired,
  password: PropTypes.string.isRequired,
  
  expandForm: PropTypes.bool.isRequired,
  isNewGuestUpdatedPage: PropTypes.bool.isRequired,
  createAccount: PropTypes.func.isRequired,
  updateCreateAccountDetails: PropTypes.func.isRequired,
  setCreateAccountStatus: PropTypes.func.isRequired,
  showError: PropTypes.bool.isRequired,
  
  passwordRules: PropTypes.shape({
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    minUpper: PropTypes.number,
    minLower: PropTypes.number,
    minNumeric: PropTypes.number,
    minSpecial: PropTypes.number,
    minEntropy: PropTypes.number,
  }),
  
};

export default connect(mapStateToProps, {
  createAccount,
  updateCreateAccountDetails,
  setCreateAccountStatus,
})(CreateAccountForm);
